import { CommonModule } from '@angular/common';
import { Component, OnInit, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { TableColumn } from '@swimlane/ngx-datatable';
import { distinctUntilChanged, finalize, take } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { TeachCompensationService } from 'src/app/services/api/teach-compensation/teach-compensation.service';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import {
  CompensationSummaryErpApprovedRequest,
  CompensationSummaryErpComparisonDetail,
  CompensationSummaryErpComparisonTable,
  CompensationSummaryErpComparisonTableRow,
  CompensationSummaryErpRowResult,
  CompensationSummaryParams,
  CompensationSummaryResponse,
} from 'src/app/services/models/teach-compensation/teach-compensation.model';
import {
  createDatatableSortParams,
  Datatable,
  DatatableSortChangeEvent,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';
import { SharedModule } from 'src/shared/shared.module';

type CompensationSummaryComparisonStatus = 'matched' | 'missing_erp' | 'not_approved' | 'missing_approved_request';
type CompensationSummaryTab = 'requests' | 'compensation';
type CompensationCourseSummaryTab = 'regular' | 'international';
type CompensationCourseSummaryRowType = 'data' | 'total';

interface CompensationSummaryComparisonRow {
  id: string;
  sequence: number;
  systemRequest: CompensationSummaryErpApprovedRequest | null;
  erpRequest: CompensationSummaryErpRowResult | null;
  leaveDate: string | null;
  comparisonStatus: CompensationSummaryComparisonStatus;
  comparisonLabel: string;
}

interface CompensationSummaryComparisonDataSource extends Datatable {
  data: CompensationSummaryComparisonRow[];
  offset: number;
}

interface CompensationCourseSummaryDataSource extends Datatable {
  data: CompensationCourseSummaryRow[];
  offset: number;
}

interface CompensationCourseSummaryRow {
  rowType: CompensationCourseSummaryRowType;
  level?: string;
  subjectCode?: string;
  subjectName?: string;
  teachingType?: string;
  studentCount: number;
  scheduledHours: number;
  makeupHours: number;
  totalHours: number;
  payableHours: number;
  rateLabel: string;
  compensationAmount: number;
}

@Component({
  selector: 'app-compensation-summary',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  templateUrl: './compensation-summary.component.html',
  styleUrl: './compensation-summary.component.scss',
})
export class CompensationSummaryComponent implements OnInit {
  @ViewChild('comparisonIndexTemplate', { static: true })
  comparisonIndexTemplate!: TemplateRef<any>;
  @ViewChild('comparisonSystemRequestTemplate', { static: true })
  comparisonSystemRequestTemplate!: TemplateRef<any>;
  @ViewChild('comparisonErpRequestTemplate', { static: true })
  comparisonErpRequestTemplate!: TemplateRef<any>;
  @ViewChild('comparisonLeaveDateTemplate', { static: true })
  comparisonLeaveDateTemplate!: TemplateRef<any>;
  @ViewChild('comparisonMakeupTeachingTemplate', { static: true })
  comparisonMakeupTeachingTemplate!: TemplateRef<any>;
  @ViewChild('comparisonStatusTemplate', { static: true })
  comparisonStatusTemplate!: TemplateRef<any>;
  @ViewChild('courseLevelTemplate', { static: true })
  courseLevelTemplate!: TemplateRef<any>;
  @ViewChild('courseSubjectCodeTemplate', { static: true })
  courseSubjectCodeTemplate!: TemplateRef<any>;
  @ViewChild('courseSubjectNameTemplate', { static: true })
  courseSubjectNameTemplate!: TemplateRef<any>;
  @ViewChild('courseStudentCountTemplate', { static: true })
  courseStudentCountTemplate!: TemplateRef<any>;
  @ViewChild('courseNumberTemplate', { static: true })
  courseNumberTemplate!: TemplateRef<any>;
  @ViewChild('coursePayableHoursTemplate', { static: true })
  coursePayableHoursTemplate!: TemplateRef<any>;
  @ViewChild('courseRateTemplate', { static: true })
  courseRateTemplate!: TemplateRef<any>;
  @ViewChild('courseCompensationAmountTemplate', { static: true })
  courseCompensationAmountTemplate!: TemplateRef<any>;

  form!: FormGroup;
  summary = signal<CompensationSummaryResponse>(this.createEmptySummary());
  isLoading = signal(false);
  hasLoadError = signal(false);
  isLoadingErpComparisonDetail = signal(false);
  hasErpComparisonDetailError = signal(false);
  activeTab = signal<CompensationSummaryTab>('requests');
  activeCourseSummaryTab = signal<CompensationCourseSummaryTab>('regular');
  readonly erpComparisonDescription =
    'รายการในตารางนี้เป็นข้อมูลที่ตรงกันจากการตรวจสอบจาก ERP เทียบกับคำขอสอนชดเชยในระบบ';
  readonly regularCourseSummaryRows: CompensationCourseSummaryRow[] = [
    {
      rowType: 'data',
      level: 'ป.ตรี',
      studentCount: 23,
      scheduledHours: 139,
      makeupHours: 4,
      totalHours: 143,
      payableHours: 0,
      rateLabel: '-',
      compensationAmount: 0,
    },
    {
      rowType: 'data',
      level: 'ป.โท-เอก',
      studentCount: 28,
      scheduledHours: 0,
      makeupHours: 0,
      totalHours: 0,
      payableHours: 0,
      rateLabel: '-',
      compensationAmount: 0,
    },
    {
      rowType: 'total',
      level: 'รวมทั้งหมด',
      studentCount: 51,
      scheduledHours: 139,
      makeupHours: 4,
      totalHours: 143,
      payableHours: 0,
      rateLabel: '-',
      compensationAmount: 0,
    },
  ];
  readonly internationalCourseSummaryRows: CompensationCourseSummaryRow[] = [
    {
      rowType: 'data',
      subjectCode: 'EN210101',
      subjectName: 'INTERNATIONAL SEMINAR',
      teachingType: 'บรรยาย/ทฤษฎี',
      level: 'อินเตอร์',
      studentCount: 18,
      scheduledHours: 0,
      makeupHours: 0,
      totalHours: 0,
      payableHours: 0,
      rateLabel: 'คำนวณตามสูตร',
      compensationAmount: 0,
    },
    {
      rowType: 'data',
      subjectCode: 'EN220201',
      subjectName: 'LAB WORKSHOP',
      teachingType: 'ปฏิบัติ',
      level: 'อินเตอร์',
      studentCount: 10,
      scheduledHours: 0,
      makeupHours: 0,
      totalHours: 0,
      payableHours: 0,
      rateLabel: 'คำนวณตามสูตร',
      compensationAmount: 0,
    },
    {
      rowType: 'total',
      subjectName: 'รวมทั้งหมด',
      teachingType: '',
      level: '',
      studentCount: 28,
      scheduledHours: 0,
      makeupHours: 0,
      totalHours: 0,
      payableHours: 0,
      rateLabel: '-',
      compensationAmount: 0,
    },
  ];
  academicTermOptions: string[] = [];
  yearOptions: string[] = [];
  comparisonColumns: TableColumn[] = [];
  courseSummaryColumns: TableColumn[] = [];
  courseSummaryTableDatasource: CompensationCourseSummaryDataSource = this.createCourseSummaryTableDatasource();
  erpComparisonDetail?: CompensationSummaryErpComparisonDetail;
  comparisonRows: CompensationSummaryComparisonRow[] = [];
  comparisonPageSizeOptions = [5, 10, 20];
  comparisonCurrentPage = 1;
  comparisonCurrentLimit = 5;
  comparisonSortParams: DatatableSortParams = { sort: null, direction: null };
  comparisonTableDatasource: CompensationSummaryComparisonDataSource = this.createComparisonTableDatasource();

  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private studyPeriodService = inject(StudyPeriodService);
  private teachCompensationService = inject(TeachCompensationService);
  private academicTerms: AcademicTermYearModel[] = [];

  ngOnInit(): void {
    this.initForm();
    this.initComparisonColumns();
    this.applyCourseSummaryTable();
    this.listenFilters();
    this.loadAcademicTerms();
  }

  formatHours(value: number): string {
    const numericValue = this.normalizeNumber(value);
    const formattedValue = Number.isInteger(numericValue)
      ? String(numericValue)
      : numericValue.toFixed(2).replace(/\.?0+$/, '');

    return `${formattedValue} ชม.`;
  }

  formatTableNumber(value: number | null | undefined): string {
    const numericValue = this.normalizeNumber(value);

    return Number.isInteger(numericValue)
      ? String(numericValue)
      : numericValue.toFixed(2).replace(/\.?0+$/, '');
  }

  formatCurrency(value: number | null | undefined): string {
    return this.formatTableNumber(value);
  }

  formatStudents(value: number | null | undefined): string {
    return `${this.formatTableNumber(value)} คน`;
  }

  formatRate(value: string | null | undefined): string {
    return value == null || value === '' ? '-' : value;
  }

  getCourseSummaryTitle(): string {
    return this.activeCourseSummaryTab() === 'regular'
      ? 'หลักสูตรปกติ'
      : 'หลักสูตรอินเตอร์';
  }

  getCourseSummaryDescription(): string {
    return this.activeCourseSummaryTab() === 'regular'
      ? 'สรุปตามระดับการศึกษา'
      : 'แสดงตามรายวิชา';
  }

  getCourseSummaryCellClass(
    row: CompensationCourseSummaryRow | undefined,
    columnClass = '',
  ): string {
    return [
      'compensation-summary-course-datatable-cell',
      row == null ? '' : `compensation-summary-course-datatable-cell-${row.rowType}`,
      columnClass,
    ].filter(Boolean).join(' ');
  }

  onCourseSummaryTabChange(tab: CompensationCourseSummaryTab): void {
    if (this.activeCourseSummaryTab() === tab) {
      return;
    }

    this.activeCourseSummaryTab.set(tab);
    this.applyCourseSummaryTable();
  }

  formatDate(value: string | null | undefined): string {
    if (value == null || value === '') {
      return '-';
    }

    const parts = value.split('-');
    if (parts.length !== 3) {
      return value;
    }

    const [year, month, day] = parts;
    return `${day}/${month}/${Number(year) + 543}`;
  }

  formatDateTime(value: string | null | undefined): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear() + 543;
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');

    return `${day}/${month}/${year} ${hour}:${minute}`;
  }

  formatRequestTitle(request: CompensationSummaryErpApprovedRequest): string {
    return [request.subject_code, request.subject_name].filter(Boolean).join(' - ') || '-';
  }

  formatRequestStatus(status: string | null | undefined): string {
    const labels: Record<string, string> = {
      approved: 'รับรองแล้ว',
      submitted: 'รอดำเนินการ',
      draft: 'รอดำเนินการ',
      rejected: 'ไม่อนุมัติ',
      revision_requested: 'ส่งกลับแก้ไข',
    };

    return status == null || status === '' ? '-' : labels[status] ?? status;
  }

  getRequestStatusBadgeClass(status: string | null | undefined): string {
    const classes: Record<string, string> = {
      approved: 'compensation-summary-request-status-badge-success',
      submitted: 'compensation-summary-request-status-badge-pending',
      draft: 'compensation-summary-request-status-badge-pending',
      rejected: 'compensation-summary-request-status-badge-danger',
      revision_requested: 'compensation-summary-request-status-badge-revision',
    };

    return status == null || status === ''
      ? 'compensation-summary-request-status-badge-neutral'
      : classes[status] ?? 'compensation-summary-request-status-badge-neutral';
  }

  formatMakeupTime(request: CompensationSummaryErpApprovedRequest): string {
    const date = this.formatDate(request.makeup_teaching_date);
    const timeRange = [request.makeup_start_time, request.makeup_end_time].filter(Boolean).join(' - ');

    return timeRange !== '' ? `${date} ${timeRange}` : date;
  }

  formatIsActive(value: boolean | null | undefined): string {
    if (value === true) {
      return 'ใช้งาน';
    }

    if (value === false) {
      return 'ยกเลิก';
    }

    return '-';
  }

  formatRowNumber(value: number | null | undefined): number | string {
    return value ?? '-';
  }

  formatLeaveDate(row: CompensationSummaryComparisonRow): string {
    return this.formatDate(row.leaveDate);
  }

  formatErpLeaveDate(row: CompensationSummaryComparisonRow): string {
    const erpRequest = row.erpRequest;
    const fallbackLeaveDate = erpRequest?.leave_date;
    const startValue = erpRequest?.erp_leave_start_date != null && erpRequest.erp_leave_start_date !== ''
      ? erpRequest.erp_leave_start_date
      : fallbackLeaveDate;
    const endValue = erpRequest?.erp_leave_end_date != null && erpRequest.erp_leave_end_date !== ''
      ? erpRequest.erp_leave_end_date
      : fallbackLeaveDate;
    const startDate = this.formatDate(startValue);
    const endDate = this.formatDate(endValue);

    if (startDate === '-' && endDate === '-') {
      return '-';
    }

    if (startDate === endDate || endDate === '-') {
      return startDate;
    }

    if (startDate === '-') {
      return endDate;
    }

    return `${startDate} - ${endDate}`;
  }

  formatMakeupTeachingDateTime(row: CompensationSummaryComparisonRow): string {
    if (row.systemRequest == null) {
      return '-';
    }

    const makeupTime = this.formatMakeupTime(row.systemRequest);
    return makeupTime !== '-' ? makeupTime : '-';
  }

  formatMakeupTeachingDate(row: CompensationSummaryComparisonRow): string {
    if (row.systemRequest == null) {
      return '-';
    }

    return this.formatDate(row.systemRequest.makeup_teaching_date);
  }

  formatMakeupTeachingTimeWithHours(row: CompensationSummaryComparisonRow): string {
    if (row.systemRequest == null) {
      return '-';
    }

    const timeRange = [row.systemRequest.makeup_start_time, row.systemRequest.makeup_end_time]
      .filter(Boolean)
      .join(' - ');

    if (timeRange === '') {
      return '-';
    }

    const hours = this.formatMakeupHours(row);
    return hours === '-' ? timeRange : `${timeRange} (${hours})`;
  }

  formatMakeupHours(row: CompensationSummaryComparisonRow): string {
    if (row.systemRequest == null) {
      return '-';
    }

    const startMinutes = this.timeToMinutes(row.systemRequest.makeup_start_time);
    const endMinutes = this.timeToMinutes(row.systemRequest.makeup_end_time);

    if (startMinutes == null || endMinutes == null || endMinutes <= startMinutes) {
      return '-';
    }

    return this.formatHours((endMinutes - startMinutes) / 60);
  }

  getComparisonBadgeClass(status: CompensationSummaryComparisonStatus): string {
    const classes: Record<CompensationSummaryComparisonStatus, string> = {
      matched: 'compensation-summary-comparison-badge-success',
      missing_erp: 'compensation-summary-comparison-badge-danger',
      not_approved: 'compensation-summary-comparison-badge-danger',
      missing_approved_request: 'compensation-summary-comparison-badge-warning',
    };

    return classes[status];
  }

  getErpComparisonTitle(): string {
    const title = ' ผลตรวจสอบข้อมูลวันลาจาก ERP';
    const academicTerm = String(this.form.get('academicTerm')?.value ?? '').trim();
    const year = String(this.form.get('year')?.value ?? '').trim();

    return academicTerm !== '' && year !== ''
      ? `${title} ${academicTerm}/${year}`
      : title;
  }

  onTabChange(tab: CompensationSummaryTab): void {
    this.activeTab.set(tab);
  }

  onComparisonPageChange(page: number): void {
    if (
      page < 1 ||
      page > this.comparisonTableDatasource.total_page ||
      page === this.comparisonCurrentPage
    ) {
      return;
    }

    this.loadErpComparisonDetail(page);
  }

  onComparisonLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit <= 0 || limit === this.comparisonCurrentLimit) {
      return;
    }

    this.comparisonCurrentLimit = limit;
    this.comparisonCurrentPage = 1;
    this.loadErpComparisonDetail(1);
  }

  onComparisonSortChange(event: DatatableSortChangeEvent): void {
    this.comparisonSortParams = createDatatableSortParams(event);
    this.comparisonCurrentPage = 1;
    this.loadErpComparisonDetail(1);
  }

  loadErpComparisonDetail(page = this.comparisonCurrentPage): void {
    if (this.isLoadingErpComparisonDetail() || !this.summary().erp_comparison.has_result) {
      return;
    }

    const hasExistingDetail = this.erpComparisonDetail != null;
    this.comparisonCurrentPage = page;

    if (!hasExistingDetail) {
      this.comparisonRows = [];
      this.comparisonTableDatasource = this.createComparisonTableDatasource();
    }

    this.isLoadingErpComparisonDetail.set(true);
    this.hasErpComparisonDetailError.set(false);

    this.teachCompensationService.getCompensationSummaryErpComparison(
      this.buildErpComparisonFilterParams(),
    ).pipe(
      finalize(() => this.isLoadingErpComparisonDetail.set(false)),
    ).subscribe({
      next: (detail) => {
        this.erpComparisonDetail = {
          ...detail,
          matched: detail.matched ?? [],
          import_unmatched: detail.import_unmatched ?? [],
          system_unmatched: detail.system_unmatched ?? [],
        };
        this.applyComparisonTable(detail.comparison_table);
      },
      error: (err) => {
        console.error(err);
        if (!hasExistingDetail) {
          this.clearErpComparisonDetail();
        }
        this.hasErpComparisonDetailError.set(true);
      },
    });
  }

  mapComparisonTableRow(row: CompensationSummaryErpComparisonTableRow): CompensationSummaryComparisonRow {
    return {
      id: String(row.id),
      sequence: row.sequence,
      systemRequest: row.system_request,
      erpRequest: row.erp_request,
      leaveDate: row.leave_date,
      comparisonStatus: row.comparison_status,
      comparisonLabel: row.comparison_label,
    };
  }

  createComparisonTableDatasource(
    rows: CompensationSummaryComparisonRow[] = [],
    page = 1,
    limit = this.comparisonCurrentLimit,
    total = rows.length,
    offset = (page - 1) * limit,
  ): CompensationSummaryComparisonDataSource {
    const totalPage = Math.max(Math.ceil(total / limit), 1);
    const normalizedPage = Math.min(Math.max(page, 1), totalPage);

    return {
      data: rows,
      page: normalizedPage,
      limit,
      total,
      total_page: totalPage,
      offset: normalizedPage === page ? offset : (normalizedPage - 1) * limit,
    };
  }

  getUnsuccessfulComparisonCount(detail: CompensationSummaryErpComparisonDetail): number {
    return this.buildComparisonRows(detail)
      .filter((row) => row.comparisonStatus !== 'matched')
      .length;
  }

  buildComparisonRows(detail: CompensationSummaryErpComparisonDetail): CompensationSummaryComparisonRow[] {
    const erpRowsByLeaveDate = this.indexErpRowsByLeaveDate(detail);
    const rows: Omit<CompensationSummaryComparisonRow, 'sequence'>[] = [];
    const consumedErpRows = new Set<CompensationSummaryErpRowResult>();

    (detail.matched ?? []).forEach((erpRow) => {
      (erpRow.approved_requests ?? []).forEach((request) => {
        rows.push({
          id: `matched-${erpRow.leave_record_id ?? erpRow.schedule_submission_id}-${request.id}`,
          systemRequest: request,
          erpRequest: erpRow,
          leaveDate: request.leave_date || erpRow.leave_date,
          comparisonStatus: 'matched',
          comparisonLabel: 'ตรงกัน',
        });
        consumedErpRows.add(erpRow);
      });
    });

    (detail.system_unmatched ?? []).forEach((request) => {
      const erpRow = erpRowsByLeaveDate.get(request.leave_date) ?? null;
      const isNotApproved = request.reason === 'คำขอยังไม่อนุมัติ';

      if (erpRow != null) {
        consumedErpRows.add(erpRow);
      }

      rows.push({
        id: `system-${request.id}`,
        systemRequest: request,
        erpRequest: erpRow,
        leaveDate: request.leave_date !== '' ? request.leave_date : erpRow?.leave_date ?? null,
        comparisonStatus: isNotApproved ? 'not_approved' : 'missing_erp',
        comparisonLabel: isNotApproved ? 'ยังไม่รับรอง' : 'ไม่พบใน ERP',
      });
    });

    (detail.import_unmatched ?? []).forEach((erpRow) => {
      if (consumedErpRows.has(erpRow)) {
        return;
      }

      rows.push({
        id: `erp-${erpRow.leave_record_id ?? erpRow.schedule_submission_id}-${erpRow.leave_date}-${erpRow.row ?? 'db'}`,
        systemRequest: null,
        erpRequest: erpRow,
        leaveDate: erpRow.leave_date,
        comparisonStatus: 'missing_approved_request',
        comparisonLabel: 'ไม่พบคำขอในระบบ',
      });
    });

    return rows
      .sort((left, right) => this.compareComparisonRows(left, right))
      .map((row, index) => ({
        ...row,
        sequence: index + 1,
      }));
  }

  private initForm(): void {
    const defaultAcademicTerm = this.getDefaultAcademicTermName();
    const defaultYear = this.getDefaultBuddhistYear();

    this.academicTermOptions = [defaultAcademicTerm];
    this.yearOptions = [defaultYear];
    this.form = this.fb.group({
      academicTerm: [defaultAcademicTerm],
      year: [defaultYear],
    });
  }

  private initComparisonColumns(): void {
    this.comparisonColumns = [
      {
        prop: 'comparisonSequence',
        name: 'ลำดับ',
        sortable: true,
        cellTemplate: this.comparisonIndexTemplate,
        width: 64,
        minWidth: 64,
        maxWidth: 64,
        cellClass: 'text-center compensation-summary-center-cell',
        headerClass: 'text-center compensation-summary-center-header',
      },
      {
        prop: 'systemRequest',
        name: 'คำขอชดเชยจากระบบ',
        sortable: true,
        cellTemplate: this.comparisonSystemRequestTemplate,
        width: 330,
        minWidth: 300,
        flexGrow: 2,
        cellClass: 'compensation-summary-comparison-detail-cell',
        headerClass: 'compensation-summary-text-header',
      },
      {
        prop: 'leaveDate',
        name: 'วันที่ขอลา/หยุด',
        sortable: true,
        cellTemplate: this.comparisonLeaveDateTemplate,
        width: 180,
        minWidth: 160,
        flexGrow: 1,
        cellClass: 'compensation-summary-left-cell',
        headerClass: 'compensation-summary-left-header',
      },
      {
        prop: 'makeupTeachingDate',
        name: 'วันขอสอนชดเชย',
        sortable: true,
        cellTemplate: this.comparisonMakeupTeachingTemplate,
        width: 220,
        minWidth: 200,
        flexGrow: 1,
        cellClass: 'text-center compensation-summary-center-cell',
        headerClass: 'text-center compensation-summary-center-header',
      },
      {
        prop: 'erpRequest',
        name: 'วันที่ขอลา/หยุด จาก ERP',
        sortable: true,
        cellTemplate: this.comparisonErpRequestTemplate,
        width: 220,
        minWidth: 200,
        flexGrow: 1,
        cellClass: 'compensation-summary-left-cell',
        headerClass: 'compensation-summary-left-header',
      },
    ];
  }

  private applyCourseSummaryTable(): void {
    const rows = this.activeCourseSummaryTab() === 'regular'
      ? this.regularCourseSummaryRows
      : this.internationalCourseSummaryRows;

    this.courseSummaryColumns = this.activeCourseSummaryTab() === 'regular'
      ? this.createRegularCourseSummaryColumns()
      : this.createInternationalCourseSummaryColumns();
    this.courseSummaryTableDatasource = this.createCourseSummaryTableDatasource(rows);
  }

  private createRegularCourseSummaryColumns(): TableColumn[] {
    return [
      {
        prop: 'level',
        name: 'ระดับ',
        sortable: false,
        cellTemplate: this.courseLevelTemplate,
        width: 170,
        minWidth: 150,
        cellClass: this.courseCellClass('compensation-summary-course-datatable-cell-text'),
        headerClass: 'text-center compensation-summary-center-header',
      },
      ...this.createSharedCourseSummaryColumns({ includeStudentCount: false }),
    ];
  }

  private createInternationalCourseSummaryColumns(): TableColumn[] {
    return [
      {
        prop: 'subjectCode',
        name: 'รหัสวิชา',
        sortable: false,
        cellTemplate: this.courseSubjectCodeTemplate,
        width: 150,
        minWidth: 140,
        cellClass: this.courseCellClass('compensation-summary-course-datatable-cell-subject'),
        headerClass: 'text-center compensation-summary-center-header',
      },
      {
        prop: 'subjectName',
        name: 'รายวิชา',
        sortable: false,
        cellTemplate: this.courseSubjectNameTemplate,
        width: 260,
        minWidth: 220,
        flexGrow: 1,
        cellClass: this.courseCellClass('compensation-summary-course-datatable-cell-subject'),
        headerClass: 'text-center compensation-summary-center-header',
      },
      {
        prop: 'teachingType',
        name: 'ประเภท',
        sortable: false,
        width: 130,
        minWidth: 120,
        cellClass: this.courseCenteredCellClass('compensation-summary-course-datatable-cell-text'),
        headerClass: 'text-center compensation-summary-center-header',
      },
      {
        prop: 'level',
        name: 'ระดับ',
        sortable: false,
        width: 110,
        minWidth: 100,
        cellClass: this.courseCenteredCellClass('compensation-summary-course-datatable-cell-text'),
        headerClass: 'text-center compensation-summary-center-header',
      },
      ...this.createSharedCourseSummaryColumns({ includeStudentCount: true }),
    ];
  }

  private createSharedCourseSummaryColumns(
    options: { includeStudentCount: boolean },
  ): TableColumn[] {
    const centeredHeaderClass = 'text-center compensation-summary-center-header';
    const columns: TableColumn[] = [
      {
        prop: 'scheduledHours',
        name: 'ชั่วโมงตามตาราง',
        sortable: false,
        cellTemplate: this.courseNumberTemplate,
        width: 150,
        minWidth: 140,
        cellClass: this.courseCenteredCellClass('compensation-summary-course-datatable-cell-number'),
        headerClass: centeredHeaderClass,
      },
      {
        prop: 'makeupHours',
        name: 'ชั่วโมงชดเชย',
        sortable: false,
        cellTemplate: this.courseNumberTemplate,
        width: 140,
        minWidth: 130,
        cellClass: this.courseCenteredCellClass('compensation-summary-course-datatable-cell-number'),
        headerClass: centeredHeaderClass,
      },
      {
        prop: 'totalHours',
        name: 'รวมชั่วโมง',
        sortable: false,
        cellTemplate: this.courseNumberTemplate,
        width: 130,
        minWidth: 120,
        cellClass: this.courseCenteredCellClass('compensation-summary-course-datatable-cell-number'),
        headerClass: centeredHeaderClass,
      },
      {
        prop: 'payableHours',
        name: 'ชั่วโมงคิดเงิน',
        sortable: false,
        cellTemplate: this.coursePayableHoursTemplate,
        width: 140,
        minWidth: 130,
        cellClass: this.courseCenteredCellClass('compensation-summary-course-datatable-cell-number'),
        headerClass: centeredHeaderClass,
      },
      {
        prop: 'rateLabel',
        name: 'อัตรา',
        sortable: false,
        cellTemplate: this.courseRateTemplate,
        width: 150,
        minWidth: 140,
        cellClass: this.courseCenteredCellClass('compensation-summary-course-datatable-cell-rate'),
        headerClass: centeredHeaderClass,
      },
      {
        prop: 'compensationAmount',
        name: 'ค่าตอบแทน',
        sortable: false,
        cellTemplate: this.courseCompensationAmountTemplate,
        width: 140,
        minWidth: 130,
        cellClass: this.courseCenteredCellClass('compensation-summary-course-datatable-cell-number'),
        headerClass: centeredHeaderClass,
      },
    ];

    if (!options.includeStudentCount) {
      return columns;
    }

    return [
      {
        prop: 'studentCount',
        name: 'จำนวนนักศึกษา',
        sortable: false,
        cellTemplate: this.courseStudentCountTemplate,
        width: 150,
        minWidth: 140,
        cellClass: this.courseCenteredCellClass('compensation-summary-course-datatable-cell-number'),
        headerClass: centeredHeaderClass,
      },
      ...columns,
    ];
  }

  private courseCellClass(columnClass: string): ({ row }: { row?: CompensationCourseSummaryRow }) => string {
    return ({ row }: { row?: CompensationCourseSummaryRow }) => (
      this.getCourseSummaryCellClass(row, columnClass)
    );
  }

  private courseCenteredCellClass(columnClass: string): ({ row }: { row?: CompensationCourseSummaryRow }) => string {
    return ({ row }: { row?: CompensationCourseSummaryRow }) => (
      `${this.getCourseSummaryCellClass(row, columnClass)} text-center compensation-summary-center-cell`
    );
  }

  private createCourseSummaryTableDatasource(
    rows: CompensationCourseSummaryRow[] = this.regularCourseSummaryRows,
  ): CompensationCourseSummaryDataSource {
    return {
      data: rows,
      page: 1,
      limit: rows.length,
      total: rows.length,
      total_page: 1,
      offset: 0,
    };
  }

  private listenFilters(): void {
    this.form.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(() => this.loadSummary());
  }

  private loadAcademicTerms(): void {
    this.studyPeriodService.getAcademicTermYears()
      .pipe(take(1))
      .subscribe({
      next: (terms) => {
        this.academicTerms = terms;
        this.academicTermOptions = this.buildAcademicTermOptions(terms);
        this.yearOptions = this.buildAcademicYearOptions(terms);
        this.ensureFilterDefaults();
        this.loadSummary();
      },
      error: (err) => {
        console.error(err);
        this.loadSummary();
      },
    });
  }

  private buildAcademicTermOptions(terms: AcademicTermYearModel[]): string[] {
    const options = Array.from(new Set(
      terms.map((term) => String(term.name).trim()).filter(Boolean),
    )).sort((a, b) => a.localeCompare(b, 'th'));

    return options.length > 0 ? options : this.academicTermOptions;
  }

  private buildAcademicYearOptions(terms: AcademicTermYearModel[]): string[] {
    const options = Array.from(new Set(
      terms.map((term) => String(term.year + 543)),
    )).sort((a, b) => Number(b) - Number(a));

    return options.length > 0 ? options : this.yearOptions;
  }

  private ensureFilterDefaults(): void {
    const academicTerm = String(this.form.get('academicTerm')?.value ?? '');
    const year = String(this.form.get('year')?.value ?? '');

    if (this.findAcademicTerm(academicTerm, year) != null || this.academicTerms.length === 0) {
      return;
    }

    const fallbackTerm = this.academicTerms[0];

    this.form.patchValue({
      academicTerm: String(fallbackTerm.name),
      year: String(this.toBuddhistYear(fallbackTerm.year)),
    }, { emitEvent: false });
  }

  private loadSummary(): void {
    this.erpComparisonDetail = undefined;
    this.comparisonRows = [];
    this.resetComparisonTableState();
    this.isLoading.set(true);
    this.hasLoadError.set(false);

    this.teachCompensationService.getCompensationSummary(
      this.buildAcademicTermFilterParams(),
    ).pipe(
      finalize(() => this.isLoading.set(false)),
    ).subscribe({
      next: (summary) => {
        const normalizedSummary = {
          total_request_count: this.normalizeNumber(summary.total_request_count),
          approved_request_count: this.normalizeNumber(summary.approved_request_count),
          pending_request_count: this.normalizeNumber(summary.pending_request_count),
          total_requested_hours: this.normalizeNumber(summary.total_requested_hours),
          approved_hours: this.normalizeNumber(summary.approved_hours),
          pending_hours: this.normalizeNumber(summary.pending_hours),
          minimum_teaching_workload: this.normalizeNumber(summary.minimum_teaching_workload),
          teaching_workload: this.normalizeNumber(summary.teaching_workload),
          makeup_teaching_workload: this.normalizeNumber(summary.makeup_teaching_workload),
          total_teaching_workload: this.normalizeNumber(summary.total_teaching_workload),
          excess_hours: this.normalizeNumber(summary.excess_hours),
          academic_term: summary.academic_term ?? null,
          erp_comparison: this.normalizeErpComparison(summary.erp_comparison),
        };

        this.summary.set(normalizedSummary);

        if (normalizedSummary.erp_comparison.has_result) {
          this.loadErpComparisonDetail();
          return;
        }

        this.clearErpComparisonDetail();
      },
      error: (err) => {
        console.error(err);
        this.hasLoadError.set(true);
        this.summary.set(this.createEmptySummary());
        this.clearErpComparisonDetail();
      },
    });
  }

  private indexErpRowsByLeaveDate(
    detail: CompensationSummaryErpComparisonDetail,
  ): Map<string, CompensationSummaryErpRowResult> {
    const rows = [...(detail.matched ?? []), ...(detail.import_unmatched ?? [])];

    return rows.reduce<Map<string, CompensationSummaryErpRowResult>>((result, row) => {
      if (row.leave_date !== '' && !result.has(row.leave_date)) {
        result.set(row.leave_date, row);
      }

      return result;
    }, new Map<string, CompensationSummaryErpRowResult>());
  }

  private compareComparisonRows(
    left: Omit<CompensationSummaryComparisonRow, 'sequence'>,
    right: Omit<CompensationSummaryComparisonRow, 'sequence'>,
  ): number {
    const leftDate = left.leaveDate ?? '';
    const rightDate = right.leaveDate ?? '';

    if (leftDate !== rightDate) {
      return leftDate.localeCompare(rightDate);
    }

    return left.id.localeCompare(right.id);
  }

  private findAcademicTerm(
    academicTermName: string,
    academicYear: string,
  ): AcademicTermYearModel | undefined {
    return this.academicTerms.find((term) => (
      String(term.name) === String(academicTermName) &&
      String(this.toBuddhistYear(term.year)) === String(academicYear)
    ));
  }

  private buildAcademicTermFilterParams(): CompensationSummaryParams {
    const { academicTerm, year } = this.form.getRawValue();
    const normalizedAcademicTerm = String(academicTerm ?? '').trim();
    const normalizedYear = String(year ?? '').trim();
    const selectedTerm = this.findAcademicTerm(normalizedAcademicTerm, normalizedYear);

    if (selectedTerm != null) {
      return { academic_term_id: selectedTerm.id };
    }

    const params: CompensationSummaryParams = {};

    if (normalizedAcademicTerm !== '') {
      params.academic_term_name = normalizedAcademicTerm;
    }

    const numericYear = Number(normalizedYear);

    if (Number.isFinite(numericYear) && numericYear > 0) {
      params.academic_term_year = this.toChristianYear(numericYear);
    }

    return params;
  }

  private buildErpComparisonFilterParams(): CompensationSummaryParams {
    return {
      ...this.buildAcademicTermFilterParams(),
      page: this.comparisonCurrentPage,
      limit: this.comparisonCurrentLimit,
      sort: this.comparisonSortParams.sort,
      direction: this.comparisonSortParams.direction,
      certification_status: 'approved',
    };
  }

  private getDefaultAcademicTermName(): string {
    const sessionAcademicTermName = String(this.authService.currentUser()?.academic_term?.name ?? '').trim();

    return sessionAcademicTermName === '' ? '1' : sessionAcademicTermName;
  }

  private getDefaultBuddhistYear(): string {
    const sessionYear = Number(this.authService.currentUser()?.academic_term?.year);

    if (!Number.isFinite(sessionYear) || sessionYear <= 0) {
      return String(this.toBuddhistYear(new Date().getFullYear()));
    }

    return String(this.toBuddhistYear(sessionYear));
  }

  private toBuddhistYear(year: number): number {
    return year > 0 && year < 2400 ? year + 543 : year;
  }

  private toChristianYear(year: number): number {
    return year >= 2400 ? year - 543 : year;
  }

  private normalizeNumber(value: unknown): number {
    const numericValue = Number(value);

    return Number.isFinite(numericValue) ? numericValue : 0;
  }

  private timeToMinutes(value: string | null | undefined): number | null {
    if (value == null || value === '') {
      return null;
    }

    const [hour, minute] = value.split(':').map((part) => Number(part));

    if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
      return null;
    }

    return (hour * 60) + minute;
  }

  private normalizeErpComparison(
    value: CompensationSummaryResponse['erp_comparison'] | null | undefined,
  ): CompensationSummaryResponse['erp_comparison'] {
    const hasResult = value?.has_result;

    if (hasResult !== true) {
      return {
        has_result: false,
        system_request_count: 0,
        system_matched_request_count: 0,
        system_unmatched_request_count: 0,
        import_unmatched_count: 0,
        imported_at: null,
        result_id: null,
      };
    }

    return {
      has_result: true,
      result_id: value.result_id ?? null,
      imported_at: value.imported_at ?? null,
      system_request_count: this.normalizeNumber(value.system_request_count),
      system_matched_request_count: this.normalizeNumber(value.system_matched_request_count),
      system_unmatched_request_count: this.normalizeNumber(value.system_unmatched_request_count),
      import_unmatched_count: this.normalizeNumber(value.import_unmatched_count),
    };
  }

  private clearErpComparisonDetail(): void {
    this.erpComparisonDetail = undefined;
    this.comparisonRows = [];
    this.resetComparisonTableState();
    this.hasErpComparisonDetailError.set(false);
  }

  private resetComparisonTableState(resetSort = true): void {
    this.comparisonCurrentPage = 1;

    if (resetSort) {
      this.comparisonSortParams = { sort: null, direction: null };
    }

    this.comparisonTableDatasource = this.createComparisonTableDatasource();
  }

  private applyComparisonTable(table: CompensationSummaryErpComparisonTable | null | undefined): void {
    if (table == null) {
      this.comparisonRows = this.filterMatchedComparisonRows(this.erpComparisonDetail == null
        ? []
        : this.buildComparisonRows(this.erpComparisonDetail));
      this.comparisonTableDatasource = this.createComparisonTableDatasource(
        this.comparisonRows,
        1,
        this.comparisonCurrentLimit,
        this.comparisonRows.length,
        0,
      );
      return;
    }

    this.comparisonRows = this.filterMatchedComparisonRows(
      table.data.map((row) => this.mapComparisonTableRow(row)),
    );
    this.comparisonCurrentPage = table.page;
    this.comparisonCurrentLimit = table.limit;
    this.comparisonTableDatasource = this.createComparisonTableDatasource(
      this.comparisonRows,
      table.page,
      table.limit,
      this.comparisonRows.length,
      0,
    );
  }

  private filterMatchedComparisonRows(
    rows: CompensationSummaryComparisonRow[],
  ): CompensationSummaryComparisonRow[] {
    return rows
      .filter((row) => row.comparisonStatus === 'matched')
      .map((row, index) => ({
        ...row,
        sequence: index + 1,
      }));
  }

  private createEmptySummary(): CompensationSummaryResponse {
    return {
      total_request_count: 0,
      approved_request_count: 0,
      pending_request_count: 0,
      total_requested_hours: 0,
      approved_hours: 0,
      pending_hours: 0,
      minimum_teaching_workload: 0,
      teaching_workload: 0,
      makeup_teaching_workload: 0,
      total_teaching_workload: 0,
      excess_hours: 0,
      academic_term: null,
      erp_comparison: {
        has_result: false,
        system_request_count: 0,
        system_matched_request_count: 0,
        system_unmatched_request_count: 0,
        import_unmatched_count: 0,
        imported_at: null,
        result_id: null,
      },
    };
  }
}
